import type { TableColumnStaticProps } from '@/types/tableTypes';
import { idLookupDisplayString } from '@/constants/lookupFilterOptions';
import { formatSummaryValue } from '@/utils/searchRowSummary';
import { readZoneDisplayString, resolveZoneLocation, splitZoneDisplayString } from '@/utils/zoneDisplayString';
import { isStatusColumn, statusWord } from '@/components/DataTable/statusChips';
import { recordTitle } from '@/composables/recordTitle';
import {
    DISPLAY_NAME_FIELD,
    RECORD_FIELD,
    titleOf,
    topLineOf,
    type RecordLayoutDefinition,
} from '@/composables/recordLayout/recordLayoutDefinition';

/**
 * The words of a record's top line and title, from its layout. The record's
 * page, the side peek and the list's pane all draw these, so a
 * record reads the same wherever it opens.
 *
 * Each field shows as plain text: a related record's name, a status's word, a
 * date in short form, "Yes" or "No". A field with no value is skipped, and so
 * is the " · " beside it. When every title field is empty, the title falls
 * back to the record's name by today's rule, so a record always has a name.
 */

export const HEADING_SEPARATOR = ' · ';

/** How the parts of a person's name join in a title: "Kareem Haag", not "Kareem · Haag". */
export const NAME_PART_SEPARATOR = ' ';

/**
 * The field names that count as a part of a person's name. A field counts when
 * its name is one of these, or ends with one after an underscore, such as
 * `contact_first_name`:
 *
 * first_name, middle_name, last_name, given_name, family_name, surname,
 * maiden_name, nickname.
 *
 * Other names do not count. `name` is a whole name, and `album_name` or
 * `genre_name` is not a person's.
 */
const NAME_PARTS: readonly string[] = [
    'first_name',
    'middle_name',
    'last_name',
    'given_name',
    'family_name',
    'surname',
    'maiden_name',
    'nickname',
];

/** Whether a field is a part of a person's name, by its field name. */
export function isNamePartField(field: string): boolean {
    return NAME_PARTS.some((part) => field === part || field.endsWith(`_${part}`));
}

/** A title of only name parts joins with a space. Every other title, and every top line, joins with " · ". */
function titleSeparatorFor(fields: string[]): string {
    return fields.length > 0 && fields.every(isNamePartField) ? NAME_PART_SEPARATOR : HEADING_SEPARATOR;
}

export interface RecordHeadingText {
    /** Empty when the layout has no top line, or none of its fields has a value. */
    topLine: string;
    title: string;
}

function nonEmpty(text: string | null | undefined): string | null {
    const trimmed = text?.trim() ?? '';
    return trimmed === '' ? null : trimmed;
}

/** A list of related records, by name: "Patio, Bar". */
function namesOf(list: unknown[]): string | null {
    const names = list
        .map((item) => (item && typeof item === 'object' ? (item as Record<string, unknown>).display_string : item))
        .filter((name): name is string | number => typeof name === 'string' || typeof name === 'number')
        .map(String);
    return nonEmpty(names.join(', '));
}

/** One column's value as text, or null when it has none. */
function columnText(column: TableColumnStaticProps, row: Record<string, unknown>): string | null {
    const value = row[column.id];
    const lookedUp = idLookupDisplayString(column.filterControl?.optionsSource, value);
    if (lookedUp !== null) return nonEmpty(lookedUp);
    if (isStatusColumn(column.id)) {
        const word = statusWord(column.id, value);
        if (word !== null) return nonEmpty(word);
    }
    if (Array.isArray(value)) return namesOf(value);
    return nonEmpty(formatSummaryValue(value, column.dataType));
}

/**
 * The zone's own name, "Front room", when the same line already names its
 * location. A zone is named "{location}-{zone}", so the whole name would say
 * the location twice. Alone, the zone keeps its whole name, since names like
 * "Front room" repeat across clients.
 */
function zoneTextBesideLocation(row: Record<string, unknown>): string | null {
    const zone = readZoneDisplayString(row.location_zone);
    const location = readZoneDisplayString(resolveZoneLocation(row.location_zone, row.location));
    return nonEmpty(splitZoneDisplayString(zone, location).zone);
}

function headingLine(
    fields: string[],
    columnsById: Map<string, TableColumnStaticProps>,
    row: Record<string, unknown>,
    noun: string,
    recordId: number | string,
    separator: string = HEADING_SEPARATOR,
): string {
    const showsLocation = fields.includes('location') && columnsById.has('location');
    return fields
        .map((field) => {
            if (field === RECORD_FIELD) return `${noun} ${recordId}`;
            if (field === 'location_zone' && showsLocation) return zoneTextBesideLocation(row);
            const column = columnsById.get(field);
            if (column) return columnText(column, row);
            // The display name is an item of its own for record types that have no such column.
            if (field === DISPLAY_NAME_FIELD) return typeof row.display_string === 'string' ? nonEmpty(row.display_string) : null;
            // A field that no longer exists is skipped.
            return null;
        })
        .filter((text): text is string => text !== null)
        .join(separator);
}

export function recordHeadingText(
    definition: RecordLayoutDefinition,
    columns: TableColumnStaticProps[],
    row: Record<string, unknown> | null,
    noun: string,
    recordId: number | string,
): RecordHeadingText {
    const source = row ?? {};
    const columnsById = new Map(columns.map((column) => [column.id, column]));
    const topLine = headingLine(topLineOf(definition, columns), columnsById, source, noun, recordId);
    const titleFields = titleOf(definition);
    const title = headingLine(titleFields, columnsById, source, noun, recordId, titleSeparatorFor(titleFields));
    return { topLine, title: title === '' ? recordTitle(row, noun, recordId) : title };
}
